import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

export default function PrivacyPage() {
  const lastUpdated = "07 July 2026";

  const privacySchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Privacy Policy — HANDICALC",
    description:
      "This Privacy Policy explains how we handle data when you use the HANDICALC® website and web application.",
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(privacySchema) }}
      />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-primary-900">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950 via-primary-900 to-primary-800"></div>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-12"
            style={{
              backgroundImage:
                "url('https://readdy.ai/api/search-image?query=Abstract%20minimalist%20landscape%20with%20soft%20rolling%20green%20hills%20and%20scattered%20pine%20trees%20in%20early%20morning%20fog%2C%20muted%20sage%20and%20olive%20tones%2C%20painterly%20atmospheric%20style%2C%20calm%20serene%20editorial%20composition%20suitable%20as%20subtle%20dark%20background%20overlay&width=1600&height=600&seq=privacy-hero-bg&orientation=landscape')",
            }}
          ></div>
          <div className="relative mx-auto max-w-content px-4 md:px-6 pt-20 md:pt-24 pb-12 md:pb-16 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur border border-background-50/20 text-xs font-medium text-accent-300">
              <i className="ri-shield-check-line"></i>
              Legal
            </span>
            <h1 className="mt-6 font-heading font-bold text-background-50 text-3xl md:text-5xl lg:text-6xl leading-tight max-w-3xl mx-auto">
              Privacy Policy
            </h1>
            <p className="mt-4 text-primary-100 text-sm md:text-base">
              Last updated: {lastUpdated}
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-4 md:px-6">
            <div className="space-y-8 md:space-y-10">
              <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                This Privacy Policy explains how we handle data when you use the HANDICALC® website and web application.
              </p>

              {/* 1. Overview */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  1. Overview
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  HANDICALC® is an independent online tool designed to help golfers and organisers calculate playing handicaps and shot allowances in line with World Handicap System™ principles. References to &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo; refer to the operator of this website.
                </p>
              </div>

              {/* 2. Data stored on your device */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  2. Data stored on your device
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-4">
                  When using the HANDICALC® calculator, certain information is stored locally on your device to provide core functionality. This data remains on your device and is not transmitted to our servers.
                </p>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-2">
                  Locally stored data may include:
                </p>
                <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                  <li>Your profile (name and handicap index)</li>
                  <li>Favourite courses</li>
                  <li>Saved golfer information (names and handicap indexes)</li>
                  <li>Custom courses you create</li>
                  <li>App settings (such as country selection and display preferences)</li>
                  <li>Cached course data for offline access</li>
                </ul>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mt-4">
                  This information is stored using your browser&rsquo;s local storage and IndexedDB.
                </p>
              </div>

              {/* 3. No account or login */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  3. No account or login
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  HANDICALC® does not require users to create an account or log in. All handicap calculations are performed locally in your browser.
                </p>
              </div>

              {/* 4. Anonymous analytics */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  4. Anonymous, cookie-free analytics
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-4">
                  We use privacy-respecting, cookie-free analytics to understand overall website usage and improve performance. No cookies are stored on your device for analytics purposes and no personal identifiers are collected.
                </p>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-2">
                  This data:
                </p>
                <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                  <li>Is aggregated and does not identify individual users</li>
                  <li>May include general information such as page views, approximate geographic region, device type, and browser type</li>
                  <li>Does not include names, handicap data, or any locally stored information</li>
                  <li>Does not use advertising, remarketing, profiling, or cross-site tracking</li>
                </ul>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mt-4">
                  We collect this limited, anonymous data under legitimate interest to understand aggregate usage and improve site performance.
                </p>
              </div>

              {/* 5. Course data */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  5. Course data
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  Course rating, slope and tee information is downloaded from our servers and cached locally on your device for performance purposes. This data relates to golf course information only and does not contain personal data.
                </p>
              </div>

              {/* 6. Your control */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  6. Your control
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed mb-2">
                  You remain in control of your data:
                </p>
                <ul className="space-y-2 ml-5 list-disc text-sm md:text-[15px] text-foreground-700 leading-relaxed">
                  <li>All personal information is stored locally on your device</li>
                  <li>You can edit or delete your profile at any time</li>
                  <li>You can remove all stored data using the &ldquo;Clear All My Data&rdquo; option in Settings</li>
                  <li>Clearing your browser storage will remove all HANDICALC® data</li>
                </ul>
              </div>

              {/* 7. Data security */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  7. Data security
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  Because personal information is stored locally on your device and not transmitted to our servers, you retain control of that data. However, you are responsible for managing your own device security and browser settings.
                </p>
              </div>

              {/* 8. Changes to this policy */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  8. Changes to this policy
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  We may update this Privacy Policy from time to time. Any changes will be published on this page with an updated revision date.
                </p>
              </div>

              {/* 9. Contact */}
              <div>
                <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl mb-4">
                  9. Contact
                </h2>
                <p className="text-foreground-700 text-sm md:text-[15px] leading-relaxed">
                  If you have questions about this Privacy Policy, please contact us via the{" "}
                  <Link
                    to="/contact"
                    className="text-primary-700 hover:text-primary-800 font-medium underline underline-offset-2"
                  >
                    contact page
                  </Link>
                  .
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-background-50 pb-16 md:pb-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src="https://readdy.ai/api/search-image?query=Peaceful%20golf%20course%20at%20first%20light%20with%20dew%20on%20fairway%20and%20soft%20morning%20haze%2C%20solitary%20flag%20in%20distance%2C%20painterly%20editorial%20style%2C%20muted%20emerald%20green%20and%20warm%20neutral%20earth%20tones%2C%20calm%20contemplative%20atmosphere%20suitable%20as%20dark%20background&width=1600&height=600&seq=privacy-cta-bg&orientation=landscape"
                alt="Calm golf course at dawn"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-900/70 to-primary-800/55"></div>
              <div className="relative z-10 px-6 md:px-14 py-12 md:py-16 text-center max-w-2xl mx-auto">
                <h2 className="font-heading font-bold text-background-50 text-2xl md:text-3xl leading-tight">
                  Still have questions?
                </h2>
                <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Contact us
                  </Link>
                  <Link
                    to="/calculator"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-background-50/10 hover:bg-background-50/20 backdrop-blur border border-background-50/30 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Open the calculator
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}